/**
 * Small pieces shared by Central's panel, feed and admin page: initials
 * avatars on brand tints, relative times, and the heart confetti.
 */

import { useEffect, useRef } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { veracityColors } from '@shared/ui';

// Light brand colours only, so Midnight Charcoal initials read on all of them.
const AVATAR_TINTS = [
  veracityColors.daybreakAmber,
  veracityColors.horizonBlue,
  veracityColors.gentleHaze,
  veracityColors.morningCoral,
] as const;

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
}

const useStyles = makeStyles({
  avatar: {
    borderRadius: '50%',
    display: 'inline-grid',
    placeItems: 'center',
    flexShrink: 0,
    color: veracityColors.midnightCharcoal,
    fontWeight: 600,
  },
  burst: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 0,
    height: 0,
    pointerEvents: 'none',
    overflow: 'visible',
    zIndex: 5,
  },
  particle: {
    position: 'absolute',
    width: '7px',
    height: '7px',
    borderRadius: '2px',
    left: '-3.5px',
    top: '-3.5px',
    opacity: 0,
  },
});

export function Initials({ name, size = 30 }: { name: string; size?: number }) {
  const styles = useStyles();
  return (
    <span
      className={styles.avatar}
      aria-hidden
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.37),
        backgroundColor: AVATAR_TINTS[hash(name) % AVATAR_TINTS.length],
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** "2h", "yesterday", "3d", then a date. Short, because it sits in a card header. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const mins = Math.round((now.getTime() - then.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function recipientsLabel(people: { name: string }[]): string {
  const names = people.map((p) => p.name);
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const CONFETTI = [
  veracityColors.veracityOrange,
  veracityColors.daybreakAmber,
  veracityColors.morningCoral,
  veracityColors.horizonBlue,
  veracityColors.clarityGreen,
  veracityColors.gentleHaze,
];

/**
 * The one showy moment in Central: twenty brand-coloured particles radiating
 * from the heart when it turns ON. Web Animations, so each particle gets its
 * own random vector without generating a keyframe per particle. Skipped under
 * prefers-reduced-motion. Remount (change `burstKey`) to fire again.
 */
export function Confetti({ burstKey }: { burstKey: number }) {
  const styles = useStyles();
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (burstKey === 0 || !ref.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const particles = Array.from(ref.current.children) as HTMLElement[];
    const animations = particles.map((el, i) => {
      const angle = (i / particles.length) * Math.PI * 2 + Math.random() * 0.4;
      const dist = 30 + Math.random() * 26;
      const x = Math.cos(angle) * dist;
      const y = Math.sin(angle) * dist - 10; // slight upward bias
      const rot = Math.round(Math.random() * 360);
      return el.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
          { transform: `translate(${x}px, ${y}px) rotate(${rot}deg)`, opacity: 0 },
        ],
        { duration: 850, easing: 'cubic-bezier(.12,.7,.3,1)', fill: 'forwards' }
      );
    });
    return () => animations.forEach((a) => a.cancel());
  }, [burstKey]);

  return (
    <span ref={ref} className={styles.burst} aria-hidden>
      {Array.from({ length: 20 }, (_, i) => (
        <span key={i} className={styles.particle} style={{ backgroundColor: CONFETTI[i % CONFETTI.length] }} />
      ))}
    </span>
  );
}
